import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { 
  MapPin, Navigation, Calculator, Sun, Moon, 
  Plane, Train, Bus, Building2, ChevronDown, X, Loader2,
  Route, Clock, DollarSign
} from 'lucide-react';

const PRESET_DESTINATIONS = [
  { 
    id: 'yul', 
    labelFr: 'Aéroport Montréal-Trudeau (YUL)', 
    labelEn: 'Montreal-Trudeau Airport (YUL)',
    lat: 45.4657, lng: -73.7455, 
    icon: Plane 
  },
  { 
    id: 'gare-centrale', 
    labelFr: 'Gare Centrale (train)', 
    labelEn: 'Central Station (train)',
    lat: 45.4998, lng: -73.5668, 
    icon: Train 
  },
  { 
    id: 'gare-autobus', 
    labelFr: 'Gare d\'autobus de Montréal', 
    labelEn: 'Montreal Bus Station',
    lat: 45.5157, lng: -73.5630, 
    icon: Bus 
  },
  { 
    id: 'quebec', 
    labelFr: 'Ville de Québec', 
    labelEn: 'Quebec City',
    lat: 46.8139, lng: -71.2080, 
    icon: Building2 
  },
  { 
    id: 'ottawa', 
    labelFr: 'Ottawa', 
    labelEn: 'Ottawa',
    lat: 45.4215, lng: -75.6972, 
    icon: Building2 
  },
  { 
    id: 'toronto', 
    labelFr: 'Toronto', 
    labelEn: 'Toronto',
    lat: 43.6532, lng: -79.3832, 
    icon: Building2 
  },
];

const content = {
  fr: {
    title: 'Estimateur de tarif',
    subtitle: 'Obtenez une estimation du prix de votre course en quelques secondes.',
    departure: 'Adresse de départ',
    departurePlaceholder: 'Tapez votre adresse de départ...',
    arrival: 'Destination',
    arrivalPlaceholder: 'Tapez une adresse ou choisissez ci-dessous...',
    presetLabel: 'Destinations populaires',
    customAddress: 'Autre adresse',
    day: 'Jour (5h–23h)',
    night: 'Nuit (23h–5h)',
    calculate: 'Calculer le tarif',
    calculating: 'Calcul en cours...',
    result: 'Estimation du tarif',
    distance: 'Distance',
    duration: 'Durée estimée',
    fareEstimate: 'Tarif estimé',
    fareNote: 'Estimation basée sur les tarifs réglementés de la CTQ. Le prix final dépend du trajet exact et de la circulation. Appelez le 438-600-4440 pour confirmer.',
    fixedRate: 'Tarif fixe réglementé',
    fixedRateNote: 'Le trajet centre-ville ↔ aéroport YUL bénéficie d\'un tarif fixe réglementé par la CTQ.',
    bookNow: 'Réserver maintenant',
    callNow: 'Appeler 438-600-4440',
    clear: 'Effacer',
    min: 'min',
    noResults: 'Aucun résultat trouvé',
    searching: 'Recherche...',
    mandatoryFee: 'Redevance obligatoire incluse (1,05 $)',
  },
  en: {
    title: 'Fare Estimator',
    subtitle: 'Get an estimate for your ride in seconds.',
    departure: 'Pickup Address',
    departurePlaceholder: 'Type your pickup address...',
    arrival: 'Destination',
    arrivalPlaceholder: 'Type an address or choose below...',
    presetLabel: 'Popular destinations',
    customAddress: 'Other address',
    day: 'Day (5am–11pm)',
    night: 'Night (11pm–5am)',
    calculate: 'Calculate fare',
    calculating: 'Calculating...',
    result: 'Fare Estimate',
    distance: 'Distance',
    duration: 'Estimated duration',
    fareEstimate: 'Estimated fare',
    fareNote: 'Estimate based on CTQ regulated rates. Final price depends on actual route and traffic. Call 438-600-4440 to confirm.',
    fixedRate: 'Regulated fixed rate',
    fixedRateNote: 'The downtown ↔ YUL airport route has a fixed rate regulated by the CTQ.',
    bookNow: 'Book now',
    callNow: 'Call 438-600-4440',
    clear: 'Clear',
    min: 'min',
    noResults: 'No results found',
    searching: 'Searching...',
    mandatoryFee: 'Mandatory fee included ($1.05)',
  }
};

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function AddressInput({ value, onChange, onSelect, placeholder, label, icon: Icon, suggestions, loading, testId }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative" data-testid={testId}>
      <label className="block text-sm font-semibold text-foreground mb-2">
        <Icon className="w-4 h-4 inline mr-2 text-primary" />{label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full h-12 px-4 pr-10 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
          data-testid={`${testId}-input`}
        />
        {value && (
          <button
            onClick={() => { onChange(''); onSelect(null); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="px-4 py-3 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Recherche...</span>
            </div>
          )}
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => { onChange(s.display); onSelect(s); setOpen(false); }}
              className="w-full text-left px-4 py-3 hover:bg-muted/50 text-sm text-foreground border-b border-border/50 last:border-0 transition-colors"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <span className="font-medium">{s.display}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FareCalculator() {
  const { language } = useLanguage();
  const t = content[language] || content.fr;
  const { rates } = siteConfig;

  const [departureText, setDepartureText] = useState('');
  const [arrivalText, setArrivalText] = useState('');
  const [departureCoords, setDepartureCoords] = useState(null);
  const [arrivalCoords, setArrivalCoords] = useState(null);
  const [timeMode, setTimeMode] = useState('day');
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [depSuggestions, setDepSuggestions] = useState([]);
  const [arrSuggestions, setArrSuggestions] = useState([]);
  const [depLoading, setDepLoading] = useState(false);
  const [arrLoading, setArrLoading] = useState(false);
  const [showPresets, setShowPresets] = useState(true);

  const debouncedDep = useDebounce(departureText, 400);
  const debouncedArr = useDebounce(arrivalText, 400);

  const searchNominatim = useCallback(async (query) => {
    if (!query || query.length < 3) return [];
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + ' Montreal Canada')}&format=json&countrycodes=ca&limit=5&addressdetails=1`,
        { headers: { 'Accept-Language': language } }
      );
      const data = await res.json();
      return data.map(item => ({
        display: item.display_name.split(',').slice(0, 3).join(','),
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
      }));
    } catch {
      return [];
    }
  }, [language]);

  useEffect(() => {
    if (!debouncedDep || debouncedDep.length < 3 || departureCoords) return;
    setDepLoading(true);
    searchNominatim(debouncedDep).then(results => {
      setDepSuggestions(results);
      setDepLoading(false);
    });
  }, [debouncedDep, departureCoords, searchNominatim]);

  useEffect(() => {
    if (!debouncedArr || debouncedArr.length < 3 || arrivalCoords) return;
    setArrLoading(true);
    searchNominatim(debouncedArr).then(results => {
      setArrSuggestions(results);
      setArrLoading(false);
    });
  }, [debouncedArr, arrivalCoords, searchNominatim]);

  const handleDepartureChange = (val) => {
    setDepartureText(val);
    setDepartureCoords(null);
    setResult(null);
  };

  const handleArrivalChange = (val) => {
    setArrivalText(val);
    setArrivalCoords(null);
    setResult(null);
    setShowPresets(!val);
  };

  const handleDepartureSelect = (item) => {
    if (item) {
      setDepartureCoords({ lat: item.lat, lng: item.lng });
      setDepSuggestions([]);
    } else {
      setDepartureCoords(null);
    }
    setResult(null);
  };

  const handleArrivalSelect = (item) => {
    if (item) {
      setArrivalCoords({ lat: item.lat, lng: item.lng });
      setArrSuggestions([]);
      setShowPresets(false);
    } else {
      setArrivalCoords(null);
      setShowPresets(true);
    }
    setResult(null);
  };

  const selectPreset = (preset) => {
    const label = language === 'fr' ? preset.labelFr : preset.labelEn;
    setArrivalText(label);
    setArrivalCoords({ lat: preset.lat, lng: preset.lng });
    setShowPresets(false);
    setArrSuggestions([]);
    setResult(null);
  };

  const isDowntownToYUL = () => {
    if (!departureCoords || !arrivalCoords) return false;
    const downtownBounds = { 
      latMin: 45.495, latMax: 45.515, 
      lngMin: -73.585, lngMax: -73.555 
    };
    const yulBounds = { 
      latMin: 45.450, latMax: 45.480, 
      lngMin: -73.760, lngMax: -73.730 
    };
    const depInDowntown = departureCoords.lat >= downtownBounds.latMin && departureCoords.lat <= downtownBounds.latMax 
      && departureCoords.lng >= downtownBounds.lngMin && departureCoords.lng <= downtownBounds.lngMax;
    const arrInYUL = arrivalCoords.lat >= yulBounds.latMin && arrivalCoords.lat <= yulBounds.latMax 
      && arrivalCoords.lng >= yulBounds.lngMin && arrivalCoords.lng <= yulBounds.lngMax;
    const depInYUL = departureCoords.lat >= yulBounds.latMin && departureCoords.lat <= yulBounds.latMax 
      && departureCoords.lng >= yulBounds.lngMin && departureCoords.lng <= yulBounds.lngMax;
    const arrInDowntown = arrivalCoords.lat >= downtownBounds.latMin && arrivalCoords.lat <= downtownBounds.latMax 
      && arrivalCoords.lng >= downtownBounds.lngMin && arrivalCoords.lng <= downtownBounds.lngMax;
    return (depInDowntown && arrInYUL) || (depInYUL && arrInDowntown);
  };

  const calculateFare = async () => {
    if (!departureCoords || !arrivalCoords) return;
    setIsCalculating(true);
    setResult(null);

    try {
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${departureCoords.lng},${departureCoords.lat};${arrivalCoords.lng},${arrivalCoords.lat}?overview=false`
      );
      const data = await res.json();

      if (data.code !== 'Ok' || !data.routes || !data.routes.length) {
        setResult({ error: true });
        return;
      }

      const route = data.routes[0];
      const distanceKm = route.distance / 1000;
      const durationMin = Math.round(route.duration / 60);

      if (isDowntownToYUL()) {
        setResult({
          fixedRate: true,
          distance: distanceKm,
          duration: durationMin,
          fareDay: 49.45,
          fareNight: 56.70,
        });
      } else {
        const r = timeMode === 'day' ? rates.day : rates.night;
        const fare = r.departure + (distanceKm * r.perKm) + rates.additionalFee;
        const fareLow = fare * 0.95;
        const fareHigh = fare * 1.15;

        setResult({
          fixedRate: false,
          distance: distanceKm,
          duration: durationMin,
          fareLow: Math.round(fareLow * 100) / 100,
          fareHigh: Math.round(fareHigh * 100) / 100,
          fareBase: Math.round(fare * 100) / 100,
        });
      }
    } catch {
      setResult({ error: true });
    } finally {
      setIsCalculating(false);
    }
  };

  const formatDistance = (km) => km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  const formatFare = (val) => language === 'fr' ? `${val.toFixed(2).replace('.', ',')} $` : `$${val.toFixed(2)}`;

  return (
    <Card className="border-0 shadow-xl overflow-hidden" data-testid="fare-calculator">
      <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Calculator className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-xl">{t.title}</h3>
            <p className="text-white/80 text-sm">{t.subtitle}</p>
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-5">
        {/* Day/Night Toggle */}
        <Tabs value={timeMode} onValueChange={(v) => { setTimeMode(v); setResult(null); }}>
          <TabsList className="w-full h-12" data-testid="fare-calculator-time-tabs">
            <TabsTrigger value="day" className="flex-1 h-10 gap-2 text-sm font-semibold data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800">
              <Sun className="w-4 h-4" />
              {t.day}
            </TabsTrigger>
            <TabsTrigger value="night" className="flex-1 h-10 gap-2 text-sm font-semibold data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-800">
              <Moon className="w-4 h-4" />
              {t.night}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Departure */}
        <AddressInput
          value={departureText}
          onChange={handleDepartureChange}
          onSelect={handleDepartureSelect}
          placeholder={t.departurePlaceholder}
          label={t.departure}
          icon={MapPin}
          suggestions={depSuggestions}
          loading={depLoading}
          testId="fare-departure"
        />

        {/* Arrival */}
        <div data-testid="fare-arrival-section">
          <AddressInput
            value={arrivalText}
            onChange={handleArrivalChange}
            onSelect={handleArrivalSelect}
            placeholder={t.arrivalPlaceholder}
            label={t.arrival}
            icon={Navigation}
            suggestions={arrSuggestions}
            loading={arrLoading}
            testId="fare-arrival"
          />

          {/* Preset destinations */}
          {showPresets && !arrivalCoords && (
            <div className="mt-3" data-testid="fare-presets">
              <button
                onClick={() => setShowPresets(prev => !prev)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2"
              >
                <ChevronDown className="w-4 h-4" />
                {t.presetLabel}
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PRESET_DESTINATIONS.map((preset) => {
                  const Icon = preset.icon;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => selectPreset(preset)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
                      data-testid={`fare-preset-${preset.id}`}
                    >
                      <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                      <span className="text-sm font-medium text-foreground group-hover:text-primary truncate">
                        {language === 'fr' ? preset.labelFr : preset.labelEn}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Calculate Button */}
        <Button
          onClick={calculateFare}
          disabled={!departureCoords || !arrivalCoords || isCalculating}
          className="w-full h-12 bg-primary text-white hover:bg-primary/90 font-bold text-base"
          data-testid="fare-calculate-btn"
        >
          {isCalculating ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t.calculating}</>
          ) : (
            <><Calculator className="w-5 h-5 mr-2" />{t.calculate}</>
          )}
        </Button>

        {/* Results */}
        {result && !result.error && (
          <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-5 space-y-4 animate-in fade-in-50 duration-300" data-testid="fare-result">
            <h4 className="font-bold text-lg text-foreground flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {t.result}
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Route className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-xs text-muted-foreground">{t.distance}</div>
                  <div className="font-bold text-foreground">{formatDistance(result.distance)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <div className="text-xs text-muted-foreground">{t.duration}</div>
                  <div className="font-bold text-foreground">~{result.duration} {t.min}</div>
                </div>
              </div>
            </div>

            {result.fixedRate ? (
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="text-sm font-semibold text-amber-700 mb-2">{t.fixedRate}</div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-foreground flex items-center gap-1"><Sun className="w-3.5 h-3.5" /> {content[language]?.day || content.fr.day}</span>
                  <span className="font-black text-xl text-primary">{formatFare(result.fareDay)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-foreground flex items-center gap-1"><Moon className="w-3.5 h-3.5" /> {content[language]?.night || content.fr.night}</span>
                  <span className="font-black text-xl text-primary">{formatFare(result.fareNight)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-3">{t.fixedRateNote}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-4 border border-primary/20">
                <div className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                  {timeMode === 'day' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                  {t.fareEstimate} ({timeMode === 'day' ? t.day : t.night})
                </div>
                <div className="text-center">
                  <span className="font-black text-3xl text-primary">
                    {formatFare(result.fareLow)} – {formatFare(result.fareHigh)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">{t.mandatoryFee}</p>
              </div>
            )}

            <p className="text-xs text-muted-foreground">{t.fareNote}</p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="flex-1 bg-primary text-white hover:bg-primary/90 font-bold" data-testid="fare-result-book">
                <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">{t.bookNow}</a>
              </Button>
              <Button asChild variant="outline" className="flex-1 border-primary text-primary hover:bg-primary hover:text-white font-bold" data-testid="fare-result-call">
                <a href={siteConfig.phoneTel}>{t.callNow}</a>
              </Button>
            </div>
          </div>
        )}

        {result && result.error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center" data-testid="fare-result-error">
            <p className="text-red-700 font-medium text-sm">
              {language === 'fr' 
                ? 'Impossible de calculer l\'itinéraire. Veuillez vérifier les adresses et réessayer.'
                : 'Unable to calculate route. Please check the addresses and try again.'}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
